import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  InputAdornment,
  TextField,
} from "@mui/material";
import { formatCents } from "./zipSlotAdsHelpers";
import { MAX_TOPUP_CENTS, MIN_TOPUP_CENTS } from "./zipSlotAdsConstants";
import { PaymentMethodStatus } from "./PlaceBidDialog";

const TopUpDialog = ({
  open,
  onClose,
  submitting = false,
  error = "",
  paymentMethod = null,
  paymentMethodLoading = false,
  onSubmit,
}) => {
  const minDollars = MIN_TOPUP_CENTS / 100;
  const maxDollars = MAX_TOPUP_CENTS / 100;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { amount: "50.00" },
    mode: "onChange",
  });

  useEffect(() => {
    if (open) reset({ amount: "50.00" });
  }, [open, reset]);

  const submit = handleSubmit((data) => {
    onSubmit(data.amount);
  });

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      PaperProps={{ sx: { minWidth: { xs: 280, sm: 480 } } }}
    >
      <form onSubmit={submit}>
        <DialogTitle>Top up ad balance</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ pb: 2 }}>
            Add prepaid click budget. You will be redirected to Stripe Checkout.
            Credits appear after payment completes. Minimum{" "}
            {formatCents(MIN_TOPUP_CENTS)}, maximum {formatCents(MAX_TOPUP_CENTS)}.
          </DialogContentText>

          <PaymentMethodStatus
            paymentMethod={paymentMethod}
            loading={paymentMethodLoading}
          />

          <Controller
            name="amount"
            control={control}
            rules={{
              required: "Amount is required",
              validate: (value) => {
                const n = Number(value);
                if (!Number.isFinite(n)) return "Enter a valid number";
                if (n < minDollars) {
                  return `Minimum top-up is ${formatCents(MIN_TOPUP_CENTS)}`;
                }
                if (n > maxDollars) {
                  return `Maximum top-up is ${formatCents(MAX_TOPUP_CENTS)}`;
                }
                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                margin="dense"
                label="Top-up amount"
                type="number"
                fullWidth
                inputProps={{ min: minDollars, max: maxDollars, step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                disabled={submitting}
                error={!!errors.amount}
                helperText={errors.amount?.message}
              />
            )}
          />

          {error ? (
            <FormHelperText error sx={{ mt: 1 }}>
              {error}
            </FormHelperText>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={submitting}
            disabled={submitting || Object.keys(errors).length > 0}
          >
            Continue to checkout
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TopUpDialog;
